import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Wrench, Users, Receipt, DollarSign, AlertTriangle, TrendingUp, ArrowUpRight, ArrowDownRight, ArrowRight, UserPlus, Percent, Package, PackageX } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import api from "@/lib/api";

const quoteStatusColor = {
  pending: "bg-amber-500/15 text-amber-600", replied: "bg-blue-500/15 text-blue-600",
  accepted: "bg-emerald-500/15 text-emerald-600", declined: "bg-red-500/15 text-red-600",
  cancelled: "bg-zinc-500/15 text-zinc-600",
};

const STATUS_HEX = {
  pending: "#F59E0B", replied: "#3B82F6", accepted: "#10B981", declined: "#EF4444", cancelled: "#71717A",
  unscheduled: "#F59E0B", scheduled: "#3B82F6", in_progress: "#A855F7", completed: "#10B981",
};
const CATEGORY_HEX = ["#FFB703", "#3B82F6", "#10B981", "#A855F7", "#EF4444", "#71717A"];
const CATEGORY_LABEL = { camera: "Cameras", alarm: "Alarms", intercom: "Intercom", dvr: "DVR/NVR", accessory: "Accessories" };

function KpiCard({ label, value, prev, icon: Icon, prefix = "", suffix = "", color = "text-eagle-gold", onClick, testid }) {
  const trend = prev !== undefined ? (value - prev) : null;
  const trendPct = prev ? Math.round(((value - prev) / prev) * 100) : null;
  return (
    <Card onClick={onClick} data-testid={testid} className={`p-5 relative overflow-hidden ${onClick ? "cursor-pointer hover:border-eagle-gold hover:shadow-md" : ""} transition-all group`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`h-10 w-10 rounded-lg bg-secondary flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
        {trend !== null && trendPct !== null && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {trend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(trendPct)}%
          </div>
        )}
      </div>
      <div className="font-display text-3xl font-bold tracking-tight">{prefix}{value?.toLocaleString?.() ?? value}{suffix}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1 flex items-center gap-1">
        {label}{onClick && <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />}
      </div>
    </Card>
  );
}

function DonutCard({ title, subtitle, data, colorMap, labelMap, testid }) {
  const chartData = data.map(d => ({ name: labelMap?.[d.status || d.category] || (d.status || d.category), value: d.count, key: d.status || d.category }));
  const total = chartData.reduce((s, d) => s + d.value, 0);
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="font-display font-semibold mb-0.5">{title}</div>
      <div className="text-xs text-muted-foreground mb-3">{subtitle}</div>
      {total === 0 ? (
        <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground">No data yet</div>
      ) : (
        <div className="flex items-center gap-4">
          <ResponsiveContainer width="50%" height={160}>
            <PieChart>
              <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={68} paddingAngle={2}>
                {chartData.map((d, i) => <Cell key={i} fill={colorMap ? (colorMap[d.key] || CATEGORY_HEX[i % CATEGORY_HEX.length]) : CATEGORY_HEX[i % CATEGORY_HEX.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 space-y-1.5 min-w-0">
            {chartData.map((d, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: colorMap ? (colorMap[d.key] || CATEGORY_HEX[i % CATEGORY_HEX.length]) : CATEGORY_HEX[i % CATEGORY_HEX.length] }} />
                  <span className="capitalize truncate">{d.name.replace("_", " ")}</span>
                </div>
                <span className="font-medium shrink-0">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function DashboardTab() {
  const [s, setS] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [drawerRows, setDrawerRows] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)).catch(() => {}); }, []);

  const openDrawer = async (config) => {
    setDrawer(config); setDrawerLoading(true);
    try { const { data } = await api.get(config.endpoint, { params: config.params || {} }); setDrawerRows(data); }
    catch { setDrawerRows([]); }
    finally { setDrawerLoading(false); }
  };

  if (!s) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">A pulse on the business — click any card to drill down.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Open Quotes" value={s.open_quotes} icon={ClipboardList} color="text-amber-600" testid="kpi-open-quotes"
          onClick={() => openDrawer({ title: "Open quotes", endpoint: "/admin/quotes", params: { status: "pending" }, kind: "quotes", cta: () => nav("/admin/quotes") })} />
        <KpiCard label="Active Jobs" value={s.active_jobs} icon={Wrench} color="text-blue-600" testid="kpi-active-jobs"
          onClick={() => openDrawer({ title: "Active jobs", endpoint: "/admin/jobs", params: { status: "scheduled" }, kind: "jobs", cta: () => nav("/admin/jobs") })} />
        <KpiCard label="Customers" value={s.total_customers} icon={Users} color="text-emerald-600" testid="kpi-customers"
          onClick={() => openDrawer({ title: "Customers", endpoint: "/admin/customers", params: {}, kind: "customers", cta: () => nav("/admin/customers") })} />
        <KpiCard label="Unpaid" value={s.unpaid_invoices} icon={Receipt} color="text-red-600" testid="kpi-unpaid"
          onClick={() => openDrawer({ title: "Unpaid invoices", endpoint: "/admin/invoices", params: {}, kind: "invoices", cta: () => nav("/admin/invoices") })} />
        <KpiCard label="Revenue (Mo)" value={s.revenue_month} prev={s.revenue_prev_month} prefix="$" icon={DollarSign} color="text-eagle-gold" testid="kpi-revenue"
          onClick={() => nav("/admin/accounting")} />
        <KpiCard label="New Customers (Mo)" value={s.new_customers_month} prev={s.new_customers_prev_month} icon={UserPlus} color="text-emerald-600" testid="kpi-new-customers"
          onClick={() => nav("/admin/customers")} />
        <KpiCard label="Conversion Rate" value={s.conversion_rate} suffix="%" icon={Percent} color="text-purple-600" testid="kpi-conversion" />
        <KpiCard label="Avg Quote Value" value={s.avg_quote_value} prefix="$" icon={DollarSign} color="text-eagle-gold" testid="kpi-avg-quote" />
        <KpiCard label="Active Products" value={s.total_products} icon={Package} color="text-blue-600" testid="kpi-total-products"
          onClick={() => nav("/admin/products")} />
        <KpiCard label="Out of Stock" value={s.out_of_stock} icon={PackageX} color="text-red-600" testid="kpi-out-of-stock"
          onClick={() => nav("/admin/products")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div><div className="font-display font-semibold">Business trend</div><div className="text-xs text-muted-foreground">Quotes, jobs and revenue over the last 6 months</div></div>
            <TrendingUp className="h-4 w-4 text-eagle-gold" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={s.trend} margin={{ top: 5, right: 12, left: -6, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Legend />
              <Line type="monotone" dataKey="quotes" stroke="#FFB703" strokeWidth={2.5} name="Quotes" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="jobs" stroke="#3B82F6" strokeWidth={2.5} name="Jobs" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} name="Revenue ($)" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <div className="font-display font-semibold mb-4">Recent activity</div>
          {(!s.recent_quotes || s.recent_quotes.length === 0) ? (
            <div className="text-sm text-muted-foreground text-center py-8">Nothing yet.</div>
          ) : (
            <div className="space-y-3">
              {s.recent_quotes.map(q => (
                <button key={q.quote_id} onClick={() => nav(`/admin/quotes?open=${q.quote_id}`)} className="w-full flex items-start gap-3 pb-3 border-b border-border last:border-0 text-left hover:opacity-80 transition">
                  <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0"><ClipboardList className="h-4 w-4 text-muted-foreground" /></div>
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{q.contact_name}</div><div className="text-xs text-muted-foreground truncate">{q.address}</div></div>
                  <Badge className={quoteStatusColor[q.status] + " border-0 shrink-0 text-[10px]"}>{q.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DonutCard title="Quotes by status" subtitle="All-time breakdown" data={s.quote_status_breakdown} colorMap={STATUS_HEX} testid="donut-quote-status" />
        <DonutCard title="Jobs by status" subtitle="All-time breakdown" data={s.job_status_breakdown} colorMap={STATUS_HEX} testid="donut-job-status" />
        <DonutCard title="Catalog by category" subtitle="Active products" data={s.category_breakdown} labelMap={CATEGORY_LABEL} testid="donut-category" />
      </div>

      <Card className="p-5">
        <div className="font-display font-semibold mb-0.5">Most-requested products</div>
        <div className="text-xs text-muted-foreground mb-4">From product-linked quote requests</div>
        {(!s.top_products || s.top_products.length === 0) ? (
          <div className="text-sm text-muted-foreground text-center py-8">No product-linked quotes yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(160, s.top_products.length * 44)}>
            <BarChart data={s.top_products} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={200} stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="count" fill="#FFB703" radius={[0, 6, 6, 0]} name="Requests" barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {drawer && (
            <>
              <SheetHeader><SheetTitle className="font-display text-2xl">{drawer.title}</SheetTitle></SheetHeader>
              <div className="mt-4">
                {drawerLoading ? <div className="text-sm text-muted-foreground text-center py-8">Loading…</div> :
                  drawerRows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-8">Nothing here.</div> :
                  <div className="space-y-2">
                    {drawerRows.slice(0, 20).map((r, i) => (
                      <Card key={i} className="p-3 hover:border-eagle-gold cursor-pointer" onClick={() => { drawer.cta?.(); setDrawer(null); }} data-testid={`drawer-item-${i}`}>
                        {drawer.kind === "quotes" && (<><div className="font-medium text-sm">{r.contact_name}</div><div className="text-xs text-muted-foreground truncate">{r.address}</div><div className="text-xs mt-1">{r.num_cameras} cameras · <Badge className={quoteStatusColor[r.status] + " border-0 ml-1 text-[10px]"}>{r.status}</Badge></div></>)}
                        {drawer.kind === "jobs" && (<><div className="font-medium text-sm">{r.customer_name}</div><div className="text-xs text-muted-foreground truncate">{r.address}</div><div className="text-xs mt-1 text-eagle-gold">${(r.price || 0).toLocaleString()} · {r.status.replace("_", " ")}</div></>)}
                        {drawer.kind === "customers" && (<><div className="font-medium text-sm">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div><div className="text-xs mt-1">{r.quotes_count} quotes · {r.jobs_count} jobs</div></>)}
                        {drawer.kind === "invoices" && (<><div className="flex items-center justify-between"><div className="font-medium text-sm">{r.invoice_number}</div><Badge className={r.status === "paid" ? "bg-emerald-500/15 text-emerald-600 border-0" : "bg-amber-500/15 text-amber-600 border-0"}>{r.status}</Badge></div><div className="text-xs text-muted-foreground">{r.customer_name}</div><div className="text-sm font-semibold text-eagle-gold mt-1">${(r.amount || 0).toLocaleString()}</div></>)}
                      </Card>
                    ))}
                  </div>
                }
                {drawer.cta && (
                  <Button className="w-full mt-4 bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" onClick={() => { drawer.cta(); setDrawer(null); }} data-testid="drawer-open-full">
                    Open full view <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}